import { useState } from 'react';
import { Button, Collapse, Skeleton, Space, Tag, Typography } from 'antd';
import { MailOutlined } from '@ant-design/icons';
import { useThread, useThreads } from '../features/email/api';
import { ThreadView } from './ThreadView';
import { EmailComposer } from './EmailComposer';
import { EmptyState, LoadError } from './states';
import { fmtDate, plural } from '../utils/format';

/** Emails tab on the contact profile: threads with this contact, plus a composer. */
export function ContactEmails({ contactId, contactEmail, contactName }: { contactId: string; contactEmail: string | null; contactName: string }) {
  const threads = useThreads({ contactId, status: 'all', pageSize: 50 });
  const [compose, setCompose] = useState(false);
  const [openId, setOpenId] = useState<string | undefined>();
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <Button type="primary" icon={<MailOutlined />} onClick={() => setCompose(true)} disabled={!contactEmail} title={contactEmail ? undefined : 'This contact has no email address'}>Send email</Button>
      </div>
      {threads.isError ? (
        <LoadError error={threads.error} onRetry={threads.refetch} title="Could not load emails" />
      ) : threads.isLoading ? (
        <Skeleton active paragraph={{ rows: 3 }} />
      ) : threads.data && threads.data.items.length === 0 ? (
        <EmptyState description={contactEmail ? 'No emails with this contact yet.' : 'Add an email address to this contact to send email.'} />
      ) : (
        <Collapse
          accordion
          activeKey={openId}
          onChange={(k) => setOpenId(Array.isArray(k) ? k[0] : (k as string | undefined))}
          items={(threads.data?.items ?? []).map((t) => ({
            key: t.id,
            label: (
              <Space>
                {t.unread && <Tag color="blue">New</Tag>}
                <Typography.Text strong={t.unread}>{t.subject}</Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>{plural(t.messageCount, 'message')} · {fmtDate(t.lastMessageAt)}{t.agent ? ` · ${t.agent.name}` : ''}</Typography.Text>
              </Space>
            ),
            children: openId === t.id ? <OpenThread id={t.id} /> : null,
          }))}
        />
      )}
      <EmailComposer open={compose} onClose={() => setCompose(false)} contactId={contactId} contactEmail={contactEmail} contactName={contactName} onSent={(id) => setOpenId(id)} />
    </div>
  );
}

function OpenThread({ id }: { id: string }) {
  const thread = useThread(id);
  if (thread.isError) return <LoadError error={thread.error} onRetry={thread.refetch} title="Could not load this conversation" />;
  if (!thread.data) return <Skeleton active paragraph={{ rows: 3 }} />;
  return <ThreadView key={thread.data.id} thread={thread.data} showContactLink={false} />;
}
